import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import useBoundStore from 'store';
import { BuyApartmentFlowPaths, SellApartmentFlowPaths } from 'routes';
import moment from 'moment-timezone';
import {
  createCallRequest,
  deleteSaleAd,
  generatePrimaryApartmentReport,
  toggleNotification,
  toggleStatusNotification,
  toggleTemporary,
} from 'api';
import {
  formatApartmentDescAlternative,
  formatNullishValue,
  toMeters,
  toReadablePrice,
  toReadableSquarePrice,
} from 'lib/utils';
import { ApartmentDeal } from '../../api/hooks/use-apartment-deals';
import { MarketTypes } from '../../api/types';
import useApartment from 'api/hooks/use-apartment';
import Header from 'components/header';
import Loader from 'components/loader';
import BaseScreen from 'components/base-screen';
import IconButton from 'components/icon-button';
import DottedList from 'components/dotted-list';
import BottomSheet from 'components/bottom-sheet';
import ErrorMessage from 'components/error-message';
import ImageCarousel from 'components/image-carousel';
import Button, { ButtonProps } from 'components/button';
import BottomSheetItem from 'components/bottom-sheet-item';
import BottomSheetResult from 'components/bottom-sheet-result';
import BottomSheetItemsContainer from 'components/bottom-sheet-items-container';
import ApartmentActionBottomSheet from 'components/apartment-actions-bottom-sheet';
import { InputApartmentPriceNavigateProps } from 'screens/input-apartment-price';
import heart from 'assets/images/heart.svg';
import threeDots from 'assets/images/three-dots.svg';
import temporaryYellow from '../../assets/images/temporary-yellow.svg';
import temporary from '../../assets/images/temporary.svg';
import Status from '../../components/status';
import bellFilled from '../../assets/images/bell-filled.svg';
import bell from '../../assets/images/bell.svg';
import styles from './styles.module.scss';
import AddFacilityToFavoriteListBottomSheet from 'components/favorite-list-item/modals/addFacilityToFavoriteListBottomSheet';
import Estimate from 'screens/estimate';
import ItemsYmap from 'components/items-ymap';
import useGetMarketPrice from './useGetMarketPrice';
import ModalSupportDeal from 'screens/estimate/components/modals/modalAnalogs/inSystem/modalSupportDeal';
import { toast } from 'react-toastify';

export type ApartmentNavigateProps = {
  id: number;
  isTemporary?: boolean;
};

type ApartmentProps = {
  mode: 'buy' | 'sell';
};

export const Apartment: React.FC<ApartmentProps> = (props) => {
  const { mode } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const forceAuthorize = useBoundStore((s) => s.forceAuthorize);
  const showToast = useBoundStore((s) => s.showToast);
  const apartmentId = (useLocation().state as ApartmentNavigateProps).id;

  const user = useBoundStore((s) => s.user);
  const isClient = useMemo(() => user?.type === 'Client', [user]);

  const { apartment, isLoading, error, mutate } = useApartment(apartmentId);

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isReportGenerated, setIsReportGenerated] = useState(false);

  const [isFavBottomSheetOpen, setIsFavBottomSheetOpen] = useState(false);
  const [isEditBottomSheetOpen, setIsEditBottomSheetOpen] = useState(false);
  const [isDeleteBottomSheetOpen, setIsDeleteBottomSheetOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCallButtonLoading, setIsCallButtonIsLoading] = useState(false);
  const [isCallButtonDisabled, setIsCallButtonDisabled] = useState(false);
  const [currentTimeZone, setCurrentTimeZone] = useState<string>('');
  const lastSelectedFacilityList = useBoundStore((s) => s.lastSelectedFacilityList);

  const [isOpenModalSupport, setIsOpenModalSupport] = useState(false);

  const handleChangeModalSupport = (value: boolean) => {
    setIsOpenModalSupport(value);
  };

  const { data, isMutating, onSubmit } = useGetMarketPrice(
    handleChangeModalSupport,
    apartment?.marketType || MarketTypes.PRIMARY,
    apartmentId,
  );

  useEffect(() => {
    // Получение текущей временной зоны браузера
    const browserTimeZone: string = moment(new Date()).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    setCurrentTimeZone(browserTimeZone);
  }, []);

  const apartmentIsLoaded = !error && !isLoading;

  const buttonActions: ButtonProps[] = [];

  if (!data) {
    buttonActions.push({
      label: 'Узнать рыночную стоимость',
      onClick: () => onSubmit(),
      loading: isMutating,
      disabled: Boolean(data),
      variant: 'dark',
    });
  }

  const buttons: ButtonProps[] = apartmentIsLoaded
    ? buttonActions
    : error
    ? [
        {
          label: 'Назад',
          onClick: () => navigate(-1),
        },
      ]
    : [];

  const handleNotificationToggle = (apartment: ApartmentDeal) => async (e: React.MouseEvent) => {
    e.stopPropagation();

    await toggleNotification(apartment.id, mutate);
  };

  const handleCreateCall = async () => {
    setIsCallButtonIsLoading(true);

    const { result } = await createCallRequest();

    if (result) {
      setIsCallButtonDisabled(true);
      showToast({ message: 'Свяжемся в течение 15 минут' });
    }

    setIsCallButtonIsLoading(false);
  };

  const handleFav = async () => {
    setIsFavBottomSheetOpen(true);
  };

  const handleTemporaryToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (apartment) {
      await toggleTemporary(apartment, mutate);
    }
  };

  const handleCreateReport = async () => {
    if (!isReportGenerated && forceAuthorize()) {
      setIsGeneratingReport(true);
      const { result } = await generatePrimaryApartmentReport(
        {
          id: apartmentId,
        },
        lastSelectedFacilityList,
      );
      if (result) {
        setIsReportGenerated(true);
        showToast({ message: 'Отчёт отправлен в чат-бот' });
      } else {
        showToast({ message: 'Ошибка отправки отчёта' });
      }

      setIsGeneratingReport(false);
    }
  };

  const handleTrackPrice = (id: number) => {
    toggleStatusNotification(id).then((result) => {
      if (!!result?.result) {
        mutate();
        showToast({ message: 'Добавлен в уведомления' });
      }
    });
  };

  const apartmentDesc = apartment
    ? formatApartmentDescAlternative(apartment.rooms, apartment.area, apartment.floor)
    : '';

  return (
    <BaseScreen
      buttons={buttons}
      headerClassName={apartmentIsLoaded ? styles.header : ''}
      controls={
        mode === 'buy' && apartmentIsLoaded ? (
          <>
            <IconButton
              /*@ts-ignore*/
              onClick={handleNotificationToggle(apartment)}
              icon={apartment?.notificationEnabled ? bellFilled : bell}
            />
            {!isClient ? (
              <>
                <IconButton
                onClick={handleTemporaryToggle}
                /*@ts-ignore*/
                icon={apartment?.isSavedInTemporaryFiles ? temporaryYellow : temporary}
              />
                <IconButton icon={heart} onClick={handleFav} />
              </>
            ) : (
              <></>
            )}

          </>
        ) : (
          <>
            <Status
              status={apartment?.availabilityStatus?.key}
              statusTitle={apartment?.availabilityStatus?.translation}
            />
          </>
        )
      }
    >
      {apartment?.marketType && (
        <AddFacilityToFavoriteListBottomSheet
          onDismiss={() => setIsFavBottomSheetOpen(false)}
          isOpen={isFavBottomSheetOpen}
          marketType={apartment?.marketType as MarketTypes}
          facilityIds={[apartment.id]}
        />
      )}
      {apartment ? (
        <>
          <ImageCarousel
            topPadding
            images={apartment?.images}
            isCustomSwipeIcon
            isCarouselInDetails
            isSecondary={state.marketTypes === MarketTypes.SECONDARY}
          />

          <div className={styles.container}>
            {apartment.title && (
              <div className={styles.address}>
                <span className={styles.housingEstateName}>{apartment.housingEstate}</span>
                <span>{', ' + apartment.title}</span>
              </div>
            )}

            <div className={styles.prices}>
              <span className={styles.price}>{toReadablePrice(apartment.price)}</span>
              <span className={styles.squarePrice}>
                {toReadableSquarePrice(apartment.squarePrice)}
              </span>
            </div>

            <div className={styles.priceDynamicsContainer}>
              {/*<PriceDynamics variant='up' price={20_000} />*/}

              {mode === 'buy' && (
                <Button
                  variant='transparent'
                  label={
                    apartment?.notificationEnabled
                      ? 'Перестать следить за ценой'
                      : 'Следить за ценой'
                  }
                  className={styles.priceSubscribeButton}
                  onClick={() => handleTrackPrice(apartment?.id)}
                />
              )}
            </div>

            {apartment?.platformAd?.url && (
              <div className={styles.source}>
                <Link target={'_blank'} to={apartment?.platformAd?.url} title='Источник'>
                  {`Перейти в источник`}
                </Link>
              </div>
            )}

            <div className={styles.buttons}>
              <Button
                variant='gray'
                onClick={handleCreateReport}
                loading={isGeneratingReport}
                label={isReportGenerated ? 'Отчёт сформирован' : 'Сформировать отчёт'}
              />

              {mode === 'buy' && (
                <IconButton
                  icon={threeDots}
                  iconHeight={24}
                  iconWidth={24}
                  className={styles.dotsButton}
                  onClick={() => {
                    toast.dismiss()
                    setIsBottomSheetOpen(true)
                  }}
                />
              )}
            </div>

            <DottedList
              header='Данные о квартире'
              items={[
                {
                  label: 'Количество спален',
                  value: apartment.bedroomCount,
                },
                {
                  label: 'Коэффициент полезной площади',
                  value: '0,8',
                },
                {
                  label: 'Высота потолка',
                  value: toMeters(apartment.ceilingHeight),
                },
                {
                  label: 'Материал стен',
                  value: 'Кирпич',
                },
                {
                  label: 'Отделка',
                  value: 'Предчистовая',
                },
              ]}
              className={styles.apartmentInfoList}
            />

            <DottedList
              header='Данные о доме'
              items={[
                {
                  label: 'Название ЖК',
                  value: formatNullishValue(apartment?.housingEstate),
                },
                {
                  label: 'Дом',
                  value: formatNullishValue(apartment?.building?.title),
                },
                {
                  label: 'Срок сдачи',
                  value: formatNullishValue(apartment?.building?.commissioningQuarter),
                },
              ]}
            />

            <div className={styles.map}>
              <ItemsYmap
                items={
                  apartment?.building?.coordinates
                    ? [
                        {
                          id: 1,
                          coords: [
                            apartment?.building?.coordinates?.latitude,
                            apartment?.building?.coordinates?.longitude,
                          ],
                          selected: true,
                        },
                      ]
                    : []
                }
                isDisableDrag
                zoom={apartment?.building?.coordinates ? 15 : 11}
                isSetCenterByFirstItem={apartment?.building?.coordinates ? true : false}
              />
            </div>

            {data && apartment.id && (
              <div className={styles.estimate}>
                <Estimate
                  dataById={data}
                  marketType={apartment.marketType ?? MarketTypes.PRIMARY}
                  facilityId={apartment.id}
                />
              </div>
            )}
          </div>

          <ApartmentActionBottomSheet
            open={isBottomSheetOpen}
            onDismiss={() => setIsBottomSheetOpen(false)}
          />
        </>
      ) : isLoading ? (
        <Loader />
      ) : (
        <ErrorMessage message={error} />
      )}

      {mode === 'sell' && (
        <>
          <BottomSheet
            noScroll
            open={isEditBottomSheetOpen}
            onDismiss={() => setIsEditBottomSheetOpen(false)}
            header={<Header className={styles.bottomSheetHeader} text='Действия' />}
          >
            <BottomSheetItemsContainer>
              <BottomSheetItem
                title='Изменить цену продажи'
                onClick={() => {
                  if (apartment) {
                    const state: InputApartmentPriceNavigateProps = {
                      apartment: {
                        id: apartment.id,
                        price: apartment.price,
                        apartmentComplexTitle: apartment.housingEstate ?? '',
                        areaInSqM: apartment.area,
                        ceilingHeight: apartment.ceilingHeight,
                        floor: apartment.floor.floor,
                        floorCount: apartment.floor.total,
                        roomCount: apartment.rooms,
                        planImage: apartment.images[0]?.src,
                        loweredPrice: apartment?.loweredPrice,
                        hasMaternityCapital: apartment?.hasMaternityCapital,
                        hasBurden: apartment?.hasBurden,
                        typeId: apartment.typeId,
                      },
                      marketTypes: location.state.marketTypes,
                    };
                    navigate(SellApartmentFlowPaths.inputApartmentPrice, {
                      state: { ...state },
                    });
                  }
                }}
              />
              <BottomSheetItem
                title='Добавить дополнительные параметры'
                onClick={() => {
                  if (apartment) {
                    const state: InputApartmentPriceNavigateProps = {
                      apartment: {
                        id: apartment.id,
                        price: apartment.price,
                        apartmentComplexTitle: apartment.housingEstate ?? '',
                        areaInSqM: apartment.area,
                        ceilingHeight: apartment.ceilingHeight,
                        floor: apartment.floor.floor,
                        floorCount: apartment.floor.total,
                        roomCount: apartment.rooms,
                        planImage: apartment.images[0]?.src,
                        loweredPrice: apartment?.loweredPrice,
                        hasMaternityCapital: apartment?.hasMaternityCapital,
                        hasBurden: apartment?.hasBurden,
                        minorOwners: apartment?.minorOwners,
                      },
                      marketTypes: location.state.marketTypes,
                    };
                    navigate(SellApartmentFlowPaths.addAdditionalOptions, {
                      state: { ...state },
                    });
                  }
                }}
              />
              <BottomSheetItem
                title='Добавить документы'
                onClick={() => {
                  if (apartment) {
                    const state: InputApartmentPriceNavigateProps = {
                      apartment: {
                        id: apartment.id,
                        price: apartment.price,
                        apartmentComplexTitle: apartment.housingEstate ?? '',
                        areaInSqM: apartment.area,
                        ceilingHeight: apartment.ceilingHeight,
                        floor: apartment.floor.floor,
                        floorCount: apartment.floor.total,
                        roomCount: apartment.rooms,
                        planImage: apartment.images[0]?.src,
                        loweredPrice: apartment?.loweredPrice,
                        hasMaternityCapital: apartment?.hasMaternityCapital,
                        hasBurden: apartment?.hasBurden,
                      },
                      marketTypes: location.state.marketTypes,
                    };
                    navigate(SellApartmentFlowPaths.documents, { state });
                  }
                }}
              />
              <BottomSheetItem
                variant='danger'
                title='Удалить объект'
                onClick={() => setIsDeleteBottomSheetOpen(true)}
              />
            </BottomSheetItemsContainer>
          </BottomSheet>

          <BottomSheet
            open={isDeleteBottomSheetOpen}
            onDismiss={() => setIsDeleteBottomSheetOpen(false)}
          >
            <BottomSheetResult type='warning' header='Удалить объект'>
              <p className={styles.bold}>
                Вы уверены, что хотите удалить объект
                <br />
                {apartmentDesc}
              </p>
            </BottomSheetResult>

            <Button
              label='Удалить'
              variant='danger'
              loading={isDeleting}
              onClick={async () => {
                if (apartment) {
                  setIsDeleting(true);
                  await deleteSaleAd(apartment.id);
                  setIsDeleting(false);

                  navigate(
                    state.marketTypes === MarketTypes.SECONDARY
                      ? SellApartmentFlowPaths.apartmentSaleAdsSecondary
                      : SellApartmentFlowPaths.apartmentSaleAds,
                  );
                }
              }}
            />
            <Button
              disabled={isDeleting}
              label='Нет, спасибо'
              variant='transparent'
              onClick={() => setIsDeleteBottomSheetOpen(false)}
            />
          </BottomSheet>
        </>
      )}
      {apartmentId && (
        <ModalSupportDeal
          isOpen={isOpenModalSupport}
          facilityId={apartmentId}
          onDismiss={handleChangeModalSupport}
          title={'Заявка на расчет'}
          type='support'
          marketType={MarketTypes.PRIMARY}
        />
      )}
    </BaseScreen>
  );
};

export const ApartmentNavigation: React.FC = () => {
  const params = useParams()
  const navigate = useNavigate()
  useEffect(() => {
    let id = Number(params.id)
    if (id) {
      navigate(BuyApartmentFlowPaths.apartment, {
        state: { id: id },
        replace: true
      })
    }
  }, [params])
  return <></>
} 
